import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import * as Sentry from '@sentry/react'
import {
  Checkbox,
  Header,
  Button,
  Label,
  Stack,
  Loader,
  Text,
} from '@crossfit/ui'

import type { Affiliate } from 'types'
import * as affiliatesService from 'services/affiliates'
import { useNotification } from 'components/Notification'
import DeclineInvite from './Decline'
import style from './style.module.css'

function useRespondToInviteForm(affiliate, token) {
  const form = useForm()
  const showNotification = useNotification()
  const queryClient = useQueryClient()

  async function handleSubmit() {
    try {
      await affiliatesService.respondToInvitation({
        token,
        affiliateId: affiliate?.id,
        accept: true,
      })
      await queryClient.invalidateQueries('affiliate')
      await queryClient.invalidateQueries('subscriptions')
      showNotification(`You have accepted the invitation`)
      // https://lightrun.com/solutions/remix-run-react-router-bug-v6-usenavigate-doesnt-navigate-to-the-same-route-but-with-different-param/
      location.href =
        location.protocol + '//' + location.host + '/tools/admin/staff'
    } catch (error) {
      if (error.response?.status === 403) {
        showNotification(error.response.data.description, 'error')
      } else if (error.response?.status === 400) {
        showNotification('This invitation has already been accepted', 'error')
      } else {
        Sentry.captureException(error, {
          extra: {
            data: error.response?.data,
          },
        })

        showNotification(error.message, 'error')
        throw error
      }
    }
  }
  return {
    ...form,
    onSubmit: form.handleSubmit(handleSubmit),
    isSubmitting: form.formState.isSubmitting,
  }
}

type Props = {
  affiliate: Affiliate
  token: string
}

function InvitationForm({ affiliate, token }: Props) {
  const { watch, register, onSubmit, isSubmitting } = useRespondToInviteForm(
    affiliate,
    token,
  )

  const [showDeclineInvite, setShowDeclineInvite] = useState(false)

  return (
    <>
      {showDeclineInvite && (
        <DeclineInvite
          token={token}
          onClose={() => setShowDeclineInvite(false)}
          affiliate={affiliate}
        />
      )}

      <Header size='large' as='h2' mb={[12, 6]}>
        Congratulations!
      </Header>

      <Text mb={8}>
        You’ve been invited to join the <strong>{affiliate?.name}</strong> Team.
        To accept this invitation, review the information below.
      </Text>

      <form onSubmit={onSubmit}>
        <Stack direction='horizontal' spacing={2} mb={10}>
          <Checkbox id='terms' {...register('terms')} />
          <Label htmlFor='terms' className={style.label}>
            I have read and agree to CrossFit&apos;s{' '}
            <a
              className='cf-link'
              href='https://www.crossfit.com/affiliate-toolkit-terms-of-service/'
              target='_blank'
              rel='noreferrer'
            >
              terms of service
            </a>
            .
          </Label>
        </Stack>

        {isSubmitting ? (
          <Loader />
        ) : (
          <Stack
            spacing={4}
            direction={['horizontal', 'vertical']}
            className={style.buttons}
          >
            <Button type='submit' disabled={!watch('terms')}>
              Accept Invite
            </Button>
            <Button
              type='button'
              variant='destructive'
              onClick={() => setShowDeclineInvite(true)}
            >
              Decline Invite
            </Button>
          </Stack>
        )}
      </form>
    </>
  )
}

export default InvitationForm
